import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import type { TrendingVideo, UpdateTrendingVideoPayload } from '../../../../../infrastructure/TrendingVideoClient';

interface Props {
  show: boolean;
  onHide: () => void;
  video: TrendingVideo | null;
  onSave: (videoId: string, patch: UpdateTrendingVideoPayload) => Promise<void>;
}

export default function EditTrendingVideoModal({ show, onHide, video, onSave }: Props) {
  const [productFocus, setProductFocus] = useState('');
  const [productLink, setProductLink] = useState('');
  const [contentLink, setContentLink] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (video) {
      setProductFocus(video.productFocus);
      setProductLink(video.productLink);
      setContentLink(video.contentLink ?? '');
      setError(null);
    }
  }, [video]);

  const handleSave = async () => {
    if (!video) {
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      const patch: UpdateTrendingVideoPayload = {};
      if (productFocus.trim() !== video.productFocus) {
        patch.productFocus = productFocus.trim();
      }
      if (productLink.trim() !== video.productLink) {
        patch.productLink = productLink.trim();
      }
      const nextContentLink = contentLink.trim();
      if (nextContentLink && nextContentLink !== video.contentLink) {
        patch.contentLink = nextContentLink;
      }

      if (Object.keys(patch).length === 0) {
        onHide();
        return;
      }

      await onSave(video.videoId, patch);
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update trending video');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Trending Video</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {video && (
          <>
            <div className="mb-3">
              <video src={video.videoUrl} controls muted style={{ maxWidth: '100%', maxHeight: '220px' }} />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Product focus label</Form.Label>
              <Form.Control value={productFocus} onChange={(e) => setProductFocus(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Product link (PDP URL)</Form.Label>
              <Form.Control type="url" value={productLink} onChange={(e) => setProductLink(e.target.value)} />
              <Form.Text className="text-muted">If changed, the server revalidates the product is published.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Source link (optional)</Form.Label>
              <Form.Control type="url" value={contentLink} onChange={(e) => setContentLink(e.target.value)} />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving || !video}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
